import { useEffect, useState } from "react";
import { X } from "lucide-react";
import Button from "./button";

const splitValues = (value) =>
  String(value || "")
    .split(/[\n,]/g)
    .map((item) => item.trim())
    .filter(Boolean);

const mergeUnique = (existing = [], additions = []) =>
  Array.from(new Set([...(Array.isArray(existing) ? existing : []), ...additions]));

const FieldBlock = ({ label, value, onChange, placeholder, disabled = false, onView }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between gap-2">
      <label className="block text-sm font-semibold text-gray-700">{label}</label>
      {onView && (
        <button
          type="button"
          onClick={onView}
          className="shrink-0 rounded-lg border border-[#881337]/30 px-2.5 py-1 text-xs font-semibold text-[#881337] transition-colors hover:bg-[#881337]/5"
        >
          View
        </button>
      )}
    </div>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-[#881337] focus:ring-2 focus:ring-[#881337]/10 ${
        disabled
          ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed focus:ring-0"
          : "border-gray-200 bg-white"
      }`}
    />
    <p className="text-xs text-gray-500">Separate values with commas or new lines.</p>
  </div>
);

// Reusable popup that shows only the values belonging to a single category.
export const ViewItemsModal = ({ isOpen, onClose, title, items = [] }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-gray-100">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {items.length === 0 ? (
            <p className="text-sm text-gray-500">No values available.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {items.map((item, index) => (
                <span
                  key={`${item}-${index}`}
                  className="inline-flex items-center rounded-full border border-[#881337]/20 bg-[#881337]/5 px-3 py-1 text-sm font-medium text-[#881337]"
                >
                  {item}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function FoodConfigModal({
  isOpen,
  onClose,
  onSave,
  currentConfig,
  isSaving = false,
}) {
  const [draft, setDraft] = useState({
    dishTypes: "",
    allergens: "",
    health: "",
    belief: "",
    lifestyle: "",
    temperatureOptions: "",
    accommodationOptions: "",
  });

  const [viewModal, setViewModal] = useState({ open: false, title: "", items: [] });

  useEffect(() => {
    if (!isOpen) return;

    setDraft({
      dishTypes: "",
      allergens: "",
      health: "",
      belief: "",
      lifestyle: "",
      temperatureOptions: "",
      accommodationOptions: "",
    });
  }, [isOpen]);

  if (!isOpen) return null;

  const foodConfig = currentConfig || {};
  const dietaryRestrictions = foodConfig.dietaryRestrictions || {};

  // Display-only superset: global merged with custom (per-restaurant AI)
  // categories. Falls back to the base config when not provided. The editable
  // fields and Save still use the global base, so this is never saved back.
  const allCategories = foodConfig.allCategories || foodConfig;
  const allDietaryRestrictions = allCategories.dietaryRestrictions || {};

  const openView = (title, items) =>
    setViewModal({ open: true, title, items: Array.isArray(items) ? items : [] });
  const closeView = () => setViewModal((prev) => ({ ...prev, open: false }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const mergedConfig = {
      dishTypes: mergeUnique(foodConfig.dishTypes, splitValues(draft.dishTypes)),
      allergens: mergeUnique(foodConfig.allergens, splitValues(draft.allergens)),
      dietaryRestrictions: {
        health: mergeUnique(dietaryRestrictions.health, splitValues(draft.health)),
        belief: mergeUnique(dietaryRestrictions.belief, splitValues(draft.belief)),
        lifestyle: mergeUnique(dietaryRestrictions.lifestyle, splitValues(draft.lifestyle)),
      },
      temperatureOptions: mergeUnique(foodConfig.temperatureOptions, splitValues(draft.temperatureOptions)),
      accommodationOptions: mergeUnique(foodConfig.accommodationOptions, splitValues(draft.accommodationOptions)),
    };

    await onSave(mergedConfig);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-4xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Food Config</h2>
            <p className="text-sm text-gray-500">
              Add new options for food configuration. Existing values will be preserved.
            </p>
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-gray-100">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FieldBlock
              label="Add Dish Types"
              value={draft.dishTypes}
              onChange={(value) => setDraft((prev) => ({ ...prev, dishTypes: value }))}
              placeholder="Main Course, Appetizer, Dessert"
              onView={() => openView("Dish Types", allCategories.dishTypes)}
            />
            <FieldBlock
              label="Add Allergens"
              value={draft.allergens}
              onChange={(value) => setDraft((prev) => ({ ...prev, allergens: value }))}
              placeholder="Milk, Eggs, Shellfish"
              disabled
              onView={() => openView("Allergens", allCategories.allergens)}
            />
            <FieldBlock
              label="Add Health Restrictions"
              value={draft.health}
              onChange={(value) => setDraft((prev) => ({ ...prev, health: value }))}
              placeholder="Gluten, Dairy, Egg"
              disabled
              onView={() => openView("Health Restrictions", allDietaryRestrictions.health)}
            />
            <FieldBlock
              label="Add Belief Accommodations"
              value={draft.belief}
              onChange={(value) => setDraft((prev) => ({ ...prev, belief: value }))}
              placeholder="Kosher, Halal"
              disabled
              onView={() => openView("Belief Accommodations", allDietaryRestrictions.belief)}
            />
            <FieldBlock
              label="Add Lifestyle Accommodations"
              value={draft.lifestyle}
              onChange={(value) => setDraft((prev) => ({ ...prev, lifestyle: value }))}
              placeholder="Vegetarian, Vegan, Pescatarian"
              disabled
              onView={() => openView("Lifestyle Accommodations", allDietaryRestrictions.lifestyle)}
            />
            <FieldBlock
              label="Add Temperature Options"
              value={draft.temperatureOptions}
              onChange={(value) => setDraft((prev) => ({ ...prev, temperatureOptions: value }))}
              placeholder="Hot, Cold, Room Temperature"
              disabled
              onView={() => openView("Temperature Options", allCategories.temperatureOptions)}
            />
            <FieldBlock
              label="Add Accommodation Options"
              value={draft.accommodationOptions}
              onChange={(value) => setDraft((prev) => ({ ...prev, accommodationOptions: value }))}
              placeholder="Gluten-Free, Dairy-Free, Nut-Free"
              disabled
              onView={() => openView("Accommodation Options", allCategories.accommodationOptions)}
            />
          </div>

          {/* <div className="mt-8 rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-sm font-semibold text-gray-700">Current values</p>
            <p className="mt-1 text-xs text-gray-400">Includes global and custom (per-restaurant AI) categories.</p>
            <p className="mt-2 text-xs text-gray-500">
              Dish Types: {(allCategories.dishTypes || []).join(", ") || "None"}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Allergens: {(allCategories.allergens || []).join(", ") || "None"}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Health: {(allDietaryRestrictions.health || []).join(", ") || "None"}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Belief: {(allDietaryRestrictions.belief || []).join(", ") || "None"}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Lifestyle: {(allDietaryRestrictions.lifestyle || []).join(", ") || "None"}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Temperatures: {(allCategories.temperatureOptions || []).join(", ") || "None"}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Accommodations: {(allCategories.accommodationOptions || []).join(", ") || "None"}
            </p>
          </div> */}
        </form>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSaving} loading={isSaving}>
            Save Food Config
          </Button>
        </div>
      </div>

      <ViewItemsModal
        isOpen={viewModal.open}
        onClose={closeView}
        title={viewModal.title}
        items={viewModal.items}
      />
    </div>
  );
}
